import { useCallback, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';
import { SupplierEnum } from '@koob/enums';
import {
  getRoomPax,
  isSupplementPromotion,
  toOptionalSupplementOptions,
} from '@koob/utils';

export const getCorrectedRooms = (selectedRooms, currentSelectedRoom) => {
  const KOEDIA = 'KOEDIA';
  const isKoediaSupplier =
    selectedRooms?.[0]?.supplier?.toUpperCase() === KOEDIA;

  if (!isKoediaSupplier || selectedRooms.length === 0) {
    return selectedRooms;
  }

  const allRooms = [
    ...selectedRooms.map((r) => ({ ...r, bed: { ...r.bed } })),
    currentSelectedRoom,
  ];

  for (let i = allRooms.length - 2; i >= 0; i--) {
    const nextRoom = allRooms[i + 1];
    const compatEntry = allRooms[i].compatibleRoomCompositions?.find(
      (entry) => entry.bedChoices?.[0]?.id === nextRoom.bed?.id,
    );
    if (compatEntry?._sourceBedId) {
      allRooms[i] = {
        ...allRooms[i],
        bed: { ...allRooms[i].bed, id: compatEntry._sourceBedId },
      };
    }
  }

  return allRooms.slice(0, -1);
};

export default function CheckoutHotelRoomSelection({
  data,
  isLoading,
  selectedRooms,
  setSelectedRooms,
  currentSelectedRoom,
  setCurrentSelectedRoom,
  totalRoomNumber,
  onValidateRoom,
  onDeleteRoom,
  // Per-slot occupancy (e.g. filters.rooms). Own inventory exposes
  // minCapacity/maxCapacity instead of nested compositions, so each slot's
  // rooms are filtered by capacity when this is provided.
  roomsComposition,
  children,
}) {
  const [currentIndex, setCurrentIndex] = useState(undefined);
  const [currentPossibilityIndex, setCurrentPossibilityIndex] = useState(0);

  const handleRoomIndexChange = (index, bedIndex, possibility) => {
    setCurrentSelectedRoom({
      ...possibility,
      bed: possibility.bedChoices[bedIndex],
    });

    setCurrentIndex(index);
  };

  const validateRoom = (hotelBasketRoomCount) => {
    const roomCount = Number.isInteger(hotelBasketRoomCount)
      ? hotelBasketRoomCount
      : totalRoomNumber;
    if (roomCount !== selectedRooms.length + 1) {
      setSelectedRooms((prev) => [...prev, currentSelectedRoom]);
      setCurrentSelectedRoom(undefined);
      setCurrentIndex(undefined);
      return;
    }

    const correctedRooms = getCorrectedRooms(
      selectedRooms,
      currentSelectedRoom,
    );

    setSelectedRooms([...correctedRooms, currentSelectedRoom]);
    if (onValidateRoom) {
      onValidateRoom();
    }
  };

  const deleteRoom = (roomIndex) => {
    setSelectedRooms((prev) => prev.filter((r, i) => i !== roomIndex));
    setCurrentIndex(undefined);
    setCurrentSelectedRoom(undefined);
    if (onDeleteRoom) {
      onDeleteRoom(roomIndex);
    }
  };

  const tagsKey = (tags) => {
    return tags
      ?.map((t) => t.label?.toLowerCase()?.replace(/ /g, ''))
      ?.sort()
      ?.join('#');
  };

  const getPromotionsKey = (promos = []) => {
    const sorted = promos
      .map((promotion) => promotion.toLowerCase().replace(/\s+/g, ''))
      .sort();

    return JSON.stringify(sorted);
  };

  const areCompositionsEqual = useCallback((composition1, composition2) => {
    const isSameCompositions =
      composition1.name === composition2.name &&
      composition1.supplierCode === composition2.supplierCode &&
      composition1.source === composition2.source &&
      composition1.bedChoices[0].totalPriceWithPromo ===
        composition2.bedChoices[0].totalPriceWithPromo &&
      tagsKey(composition1.bedChoices[0].tags) ===
        tagsKey(composition2.bedChoices[0].tags) &&
      getPromotionsKey(composition1.bedChoices[0]?.promotions) ===
        getPromotionsKey(composition2.bedChoices[0]?.promotions);

    return isSameCompositions;
  }, []);

  const getRoomKey = useCallback((room, depth) => {
    return `${room.name}_${room.supplierCode || room.supplier}_${room.source}_${
      room.bedChoices[0].totalPriceWithPromo
    }_${depth}_${tagsKey(room.bedChoices[0].tags)}_${getPromotionsKey(room.bedChoices[0]?.promotions)}`;
  }, []);

  const mergeCompatibleRoomCompositions = useCallback(
    (targetRoom, compatibleRoomCompositions, depth, sourceBedId = null) => {
      targetRoom.compatibleRoomCompositions =
        targetRoom.compatibleRoomCompositions || [];
      compatibleRoomCompositions.forEach((composition) => {
        const existingComposition = targetRoom.compatibleRoomCompositions.find(
          (existingComposition) =>
            areCompositionsEqual(existingComposition, composition),
        );
        if (!existingComposition) {
          targetRoom.compatibleRoomCompositions.push({
            ...composition,
            ...(sourceBedId ? { _sourceBedId: sourceBedId } : {}),
          });
        } else if (depth > 0) {
          mergeCompatibleRoomCompositions(
            existingComposition,
            composition.compatibleRoomCompositions,
            depth - 1,
            sourceBedId,
          );
        }
      });
    },
    [areCompositionsEqual],
  );

  const wrapAvailableRoomCompositions = useCallback(
    (mergedRooms, room, depth) => {
      const key = getRoomKey(room, depth);
      if (!mergedRooms.has(key)) {
        mergedRooms.set(key, { ...room });
      } else {
        mergeCompatibleRoomCompositions(
          mergedRooms.get(key),
          room.compatibleRoomCompositions,
          depth,
          room.bedChoices[0]?.id,
        );
      }
      if (depth > 0) {
        room.compatibleRoomCompositions.forEach((composition) => {
          wrapAvailableRoomPossibilities(
            composition.compatibleRoomCompositions || [],
          );
        });
      }
    },
    [mergeCompatibleRoomCompositions, getRoomKey],
  );

  const wrapAvailableRoomPossibilities = useCallback(
    (availableRooms) => {
      const mergedRooms = new Map();
      availableRooms.forEach((room) => {
        wrapAvailableRoomCompositions(mergedRooms, room, totalRoomNumber - 1);
      });
      return Array.from(mergedRooms.values());
    },
    [wrapAvailableRoomCompositions, totalRoomNumber],
  );

  const removeDuplicateRooms = (rooms) => {
    const uniqueRoomsIndex = {};
    return rooms.filter((room) => {
      const key = `${room.name}_${
        room?.supplierCode ? room?.supplierCode : room?.supplier
      }_${room.source}_${room.bedChoices[0].totalPriceWithPromo}_${tagsKey(
        room.bedChoices[0].tags,
      )}_${getPromotionsKey(room.bedChoices[0]?.promotions)}`;

      if (!uniqueRoomsIndex[key]) {
        uniqueRoomsIndex[key] = true;
        return true;
      }
      return false;
    });
  };

  const updateOriginalCompatibilityRoom = (rooms, referencesRooms) => {
    rooms.forEach((room) => {
      if (room.compatibleRoomCompositions?.length > 0) {
        updateOriginalCompatibilityRoom(
          room.compatibleRoomCompositions,
          referencesRooms,
        );
      }

      if (room.originalIndex === undefined) {
        if (room.supplier !== SupplierEnum.KOOB) {
          const originalRoom = referencesRooms.find((rRef) => {
            if (rRef.supplier === SupplierEnum.KOOB) {
              return false;
            }
            return (
              rRef.name === room.name &&
              rRef.supplier === room.supplier &&
              rRef.source === room.source
            );
          });
          if (originalRoom) {
            room.originalIndex = originalRoom.originalIndex;
          }
        } else {
          const originalRoom = referencesRooms.find((rRef) => {
            if (rRef.supplier !== SupplierEnum.KOOB) {
              return false;
            }
            const rBedsIds = rRef.bedChoices
              .map((bed) => {
                return `${bed.promotions.join('_')}`;
              })
              .join('_');
            const bedIds = room.bedChoices
              .map((bed) => {
                return `${bed.promotions.join('_')}`;
              })
              .join('_');

            return (
              rRef.supplierSpecificInformations.hotelRoomId ===
                room.supplierSpecificInformations.hotelRoomId &&
              rBedsIds === bedIds
            );
          });

          room.originalIndex = originalRoom?.originalIndex;
          room.allPromotions = originalRoom?.allPromotions;
          room.allOptionalSupplements = originalRoom?.allOptionalSupplements;
        }
      }
    });
  };

  function groupPossibilities(data) {
    const filteredPossibilities = [];
    const datas = cloneDeep(data.possibilities);
    const originalPossibilities = datas
      .sort((a, b) => {
        const hasPromoA = !!a.bedChoices?.[0]?.promotions?.[0];
        const hasPromoB = !!b.bedChoices?.[0]?.promotions?.[0];
        return hasPromoB - hasPromoA;
      })
      .map((possibility, index) => {
        possibility.originalIndex = index;
        if (possibility.supplier === SupplierEnum.KOOB) {
          filteredPossibilities.push(possibility);
        }

        return possibility;
      });

    const groupedPossibilities = {};

    filteredPossibilities.forEach((possibility) => {
      const key = `${possibility?.supplierSpecificInformations?.hotelRoomId}`;
      if (!groupedPossibilities[key]) {
        groupedPossibilities[key] = {
          ...possibility,
          allPromotions: {},
          originalIndexes: [],
        };
      }
      possibility.bedChoices.forEach((bedChoice) => {
        if (!isSupplementPromotion(bedChoice)) {
          // Prefix the promo id so the outer key is not an integer-like
          // string, otherwise Object.values() would auto-sort numerically
          // and break the API ordering (which puts cumulative combos first).
          const promoKey = `p${bedChoice.id.split('##').pop()}`;
          if (!groupedPossibilities[key].allPromotions[promoKey]) {
            groupedPossibilities[key].allPromotions[promoKey] = [
              { [bedChoice.id]: bedChoice.promotions },
            ];
          } else {
            groupedPossibilities[key].allPromotions[promoKey].push({
              [bedChoice.id]: bedChoice.promotions,
            });
          }
          if (
            groupedPossibilities[key].originalIndexes.indexOf(
              possibility.originalIndex,
            ) === -1
          ) {
            groupedPossibilities[key].originalIndexes.push(
              possibility.originalIndex,
            );
          }
        }
      });
    });

    const newPossibilities = Object.values(groupedPossibilities).map(
      (possibility) => {
        const allPromotions = Object.values(possibility.allPromotions).flat();
        delete possibility.allPromotions;
        return { ...possibility, allPromotions };
      },
    );

    newPossibilities.forEach((possibility) => {
      originalPossibilities.forEach((originalPossibility) => {
        if (
          possibility.originalIndexes.includes(
            originalPossibility.originalIndex,
          )
        ) {
          originalPossibility.allPromotions = possibility.allPromotions;
        }
      });
    });

    const remainingPossibilitiesKoedia = originalPossibilities.filter(
      (possibility) => possibility.supplier === SupplierEnum.KOEDIA,
    );

    const remainingPossibilitiesRakuten = originalPossibilities.filter(
      (possibility) => possibility.supplier === SupplierEnum.RAKUTEN,
    );

    const remainingPossibilitiesStaah = originalPossibilities.filter(
      (possibility) => possibility.supplier === SupplierEnum.STAAH,
    );

    const wrappedRemainingPossibilitiesKoedia = wrapAvailableRoomPossibilities(
      remainingPossibilitiesKoedia,
    );

    updateOriginalCompatibilityRoom(
      originalPossibilities,
      originalPossibilities,
    );

    const newOriginalPossibilities = [
      ...newPossibilities,
      ...remainingPossibilitiesRakuten,
      ...wrappedRemainingPossibilitiesKoedia,
      ...remainingPossibilitiesStaah,
    ].sort((a, b) => a.originalIndex - b.originalIndex);

    const updatedPossibilitiesList = newOriginalPossibilities.map((item) => {
      const composition = item;
      // A supplement is a surcharge, not a discount: the room is quoted at its
      // without-promo amount. Rewritten in place so every reader downstream —
      // the cards, the selection payload, the price handed back on save — sees
      // the one price. `getBedChoicePrice` states the same rule for the callers
      // that price the raw availability instead, such as the trip designer's
      // room suggestion.
      composition?.bedChoices.forEach((bedChoice) => {
        if (isSupplementPromotion(bedChoice)) {
          bedChoice.totalPriceWithPromo = bedChoice.totalPriceWithoutPromo;
        }
      });
      return composition;
    });
    const cleanedPossibilities = removeDuplicateRooms(updatedPossibilitiesList);
    return { original: originalPossibilities, new: cleanedPossibilities };
  }

  function selectedRoomsFromPromotionSelect(
    originalIndex,
    bedChoicePromo,
    bedName,
  ) {
    const room = initialPossibilities
      .filter((possibility) => {
        return possibility.originalIndex === originalIndex;
      })
      .pop();
    const rooms = initialPossibilities.filter((possibility) => {
      return (
        possibility?.supplierSpecificInformations?.hotelRoomId ===
        room?.supplierSpecificInformations?.hotelRoomId
      );
    });

    let found = false;
    for (let possibility of rooms) {
      if (found) {
        break;
      }
      for (let i = 0; i < possibility.bedChoices.length; i++) {
        let bed = possibility.bedChoices[i];
        if (
          bed.name === bedName &&
          bed.promotions.join(', ') === bedChoicePromo
        ) {
          handleRoomIndexChange(possibility.originalIndex, i, possibility);
          found = true;
          break;
        }
      }
    }
  }

  // What the availability offers, before anything is selected.
  const basePossibilities = useMemo(() => {
    if (!data?.possibilities) {
      return { original: [], new: [] };
    }
    return groupPossibilities(cloneDeep(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, totalRoomNumber]);

  // The offered rooms are DERIVED from the availability and from what is
  // already selected, never stored. Held in state and only (re)built while
  // nothing was selected, this list stayed empty as soon as a selection reached
  // the component in the same pass as the availability — reopening on an
  // already-composed stay showed the chosen rooms and nothing else to pick
  // from.
  const { new: possibilities, original: initialPossibilities } = useMemo(() => {
    const firstSupplier = selectedRooms?.[0]?.supplier;
    const isKoediaSupplier = firstSupplier === SupplierEnum.KOEDIA;
    const isRakutenSupplier = firstSupplier === SupplierEnum.RAKUTEN;
    const isStaahSupplier = firstSupplier === SupplierEnum.STAAH;
    // Own inventory (KOOB) drops nested compositions: the next slot's rooms come
    // from the same flat list, filtered by capacity at render time, so there is
    // nothing to re-derive after a selection.
    const isOwnInventory = firstSupplier === SupplierEnum.KOOB;
    // Narrowing describes what the NEXT slot may be, so it only applies while
    // there is one. Every slot filled — a restored selection being edited —
    // keeps the whole availability offered, which is what a replacement is
    // picked from.
    const hasNextSlot =
      !(totalRoomNumber > 0) || selectedRooms.length < totalRoomNumber;

    if (selectedRooms.length === 0 || isOwnInventory || !hasNextSlot) {
      return basePossibilities;
    }

    // One narrowing step per room already chosen: each one restricts the offer
    // to the compositions it is compatible with. KOEDIA, RAKUTEN and STAAH
    // chain from the previous step; anything else restarts from the full
    // availability, as the effect this replaces did.
    const chainsFromPreviousStep =
      isKoediaSupplier || isRakutenSupplier || isStaahSupplier;
    let current = basePossibilities;

    for (const selectedRoom of selectedRooms) {
      const source = chainsFromPreviousStep
        ? current.new
        : cloneDeep(data)?.possibilities;

      const possibility = source?.find(
        (item) =>
          item.name === selectedRoom.name &&
          (!isKoediaSupplier ||
            (tagsKey(item.bedChoices[0].tags) ===
              tagsKey(selectedRoom.bedChoices[0].tags) &&
              getPromotionsKey(item.bedChoices[0].promotions) ===
                getPromotionsKey(selectedRoom.bedChoices[0].promotions))),
      );

      // Nothing matched: keep what the previous step offered rather than
      // emptying the list.
      if (!possibility) {
        break;
      }

      current = groupPossibilities({
        possibilities: possibility.compatibleRoomCompositions ?? [],
      });
    }

    return current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePossibilities, data, selectedRooms, totalRoomNumber]);

  // Every supplement of the availability, all rooms together: the cards use it
  // to keep supplement NAMES out of the promotion select. What a supplement
  // costs is never read from here — it is priced off the bed being shown, since
  // a percentage supplement is worth a different amount on each room.
  const promoList = (data?.possibilities || []).flatMap((choice) =>
    (choice?.bedChoices ?? []).flatMap((bedChoice) =>
      toOptionalSupplementOptions(bedChoice?.optionalSupplements),
    ),
  );

  // Own inventory exposes minCapacity/maxCapacity instead of nested
  // compositions: only offer rooms whose capacity fits the current slot's
  // occupancy. Rooms without capacity fields (third-party) always pass.
  const currentSlot = roomsComposition?.[selectedRooms.length];
  const currentSlotPax = getRoomPax(currentSlot);
  const availableRooms = currentSlotPax
    ? possibilities.filter((room) => {
        const min = room?.minCapacity ?? 0;
        const max = room?.maxCapacity ?? Infinity;
        return currentSlotPax >= min && currentSlotPax <= max;
      })
    : possibilities;

  return children({
    isLoading,
    selectedRooms,
    availableRooms,
    deleteRoom,
    currentIndex,
    handleRoomIndexChange,
    totalRoomNumber,
    currentSelectedRoom,
    // KD-3496 — the room in progress is re-priced from the card: ticking an
    // optional supplement folds it into `currentSelectedRoom.bed`. Handing out
    // the room without its setter left every caller with a no-op fallback, so a
    // ticked supplement changed neither the displayed price nor what gets
    // validated.
    setCurrentSelectedRoom,
    validateRoom,
    selectedRoomsFromPromotionSelect,
    initialPossibilities,
    promoList,
    currentPossibilityIndex,
    setCurrentPossibilityIndex,
  });
}
